/**
 * @file 読書の候補一覧 (よむ) のクライアント。
 *
 * @what: `GET /families/{familyId}/study/reading_catalogs` の取得と、最新の版を選ぶ判断。
 * @why: 候補一覧は家族が取り込むデータで、アプリには同梱しない (epic_23 §5)。段階の記号は
 *   出典ごとの語彙なのでサーバは意味も順序も持たず、並びは受け取った順をそのまま使う。
 * @failure: ApiError を伝播。空配列 = まだ何も取り込んでいない、は正常系。
 */

import { apiFetch } from "@/shared/api/client";

export interface StudyReadingBook {
  /** 出典の段階の記号 ("5A" / "4A" / ...)。level + no でカタログ内の 1 冊を指す。 */
  level: string;
  /** その段階の中の通し番号 (1 始まり)。 */
  no: number;
  title: string;
  author?: string;
  publisher?: string;
  /** 表紙を引く鍵。無ければ頭文字の枠になる。 */
  isbn?: string;
  /** 1 冊に落ちないものの注記 (「3冊の組」など)。 */
  note?: string;
  is_new?: boolean;
  used_in_material?: boolean;
}

export interface StudyReadingCatalog {
  id: string;
  schema_v: number;
  /** 出典を束ねる id。同じ series_id の中で order 最大が最新の版。 */
  series_id: string;
  order: number;
  title: string;
  /** 出典の名義 (発行者名)。画面に出す。 */
  source_name: string;
  source_url?: string;
  edition?: string;
  books: StudyReadingBook[];
  created_at: string;
  updated_at: string;
}

export async function fetchReadingCatalogs(
  familyId: string,
  signal?: AbortSignal,
): Promise<StudyReadingCatalog[]> {
  const res = await apiFetch<{ catalogs: StudyReadingCatalog[] }>(
    `/families/${encodeURIComponent(familyId)}/study/reading_catalogs`,
    signal ? { signal } : {},
  );
  return res.catalogs;
}

/**
 * 出典ごとに最新の版だけを残す。
 *
 * @what: 同じ `series_id` の中で `order` が最大のものを選ぶ。`order` が同点なら id の
 *   辞書順で後ろのものを採る (順序を決める根拠が無いので、どちらを採るかを固定するだけ)。
 * @why: 年度版が変わっても古い版を消さない (過去の記録が古い版を指したままでも読めるように
 *   するため)。候補として出すのは最新の版だけなので、選別はここで行う。
 */
export function latestCatalogs(catalogs: readonly StudyReadingCatalog[]): StudyReadingCatalog[] {
  const best = new Map<string, StudyReadingCatalog>();
  for (const c of catalogs) {
    const current = best.get(c.series_id);
    const sameOrderAndLater = c.order === current?.order && c.id.localeCompare(current.id) > 0;
    if (!current || c.order > current.order || sameOrderAndLater) {
      best.set(c.series_id, c);
    }
  }
  return [...best.values()].sort((a, b) => a.series_id.localeCompare(b.series_id));
}

/**
 * カタログに出てくる段階を、本の並び順のまま重複なく取り出す。
 *
 * @why: 段階の順序はサーバも web も知らない (出典ごとに語彙が違う)。出典が並べた順が正なので、
 *   本の配列に出てきた順をそのまま段階の並びにする。
 */
export function levelsOf(catalog: StudyReadingCatalog): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const b of catalog.books) {
    if (!seen.has(b.level)) {
      seen.add(b.level);
      out.push(b.level);
    }
  }
  return out;
}
